import type { LucideIcon } from 'lucide-react';
import {
	CalendarCheck,
	Ear,
	HeartHandshake,
	House,
	Sparkles,
	Sprout,
	Target,
	Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
	getApproach,
	getFocusTopic,
	getHighlightTag,
	type HighlightTagMeta,
} from '@/constants/professionalTags';

/** Mapa local dos nomes de ícone do catálogo para os componentes lucide. */
export const HIGHLIGHT_TAG_ICONS: Record<HighlightTagMeta['icon'], LucideIcon> =
	{
		sparkles: Sparkles,
		'calendar-check': CalendarCheck,
		target: Target,
		'heart-handshake': HeartHandshake,
		ear: Ear,
		users: Users,
		home: House,
		sprout: Sprout,
	};

type ChipSize = 'sm' | 'md';

interface HighlightTagChipProps {
	tag?: string | null;
	size?: ChipSize;
	className?: string;
}

/** Chip colorido da tag de destaque (ex.: "Acessível"). Não renderiza nada sem tag válida. */
const HighlightTagChip = ({
	tag,
	size = 'sm',
	className,
}: HighlightTagChipProps) => {
	const meta = getHighlightTag(tag);
	if (!meta) return null;
	const Icon = HIGHLIGHT_TAG_ICONS[meta.icon];

	return (
		<span
			title={meta.hint}
			className={cn(
				'inline-flex shrink-0 items-center gap-1 rounded-full font-black uppercase tracking-[0.12em]',
				size === 'sm'
					? 'px-2.5 py-1 text-[10px]'
					: 'px-3 py-1.5 text-[11px]',
				meta.chipClassName,
				className,
			)}
		>
			<Icon
				className={size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'}
				strokeWidth={2.5}
			/>
			{meta.label}
		</span>
	);
};

/** "TCC · ACT" a partir da abordagem principal e complementares (nomes curtos). */
const formatApproachShortLine = (
	main?: string | null,
	secondary?: string[] | null,
) =>
	[main, ...(secondary ?? [])]
		.map((key) => getApproach(key)?.short)
		.filter((value, index, array): value is string =>
			Boolean(value && array.indexOf(value) === index),
		)
		.join(' · ');

export interface ProfessionalTagChipsProps {
	highlight_tag?: string | null;
	main_approach?: string | null;
	secondary_approaches?: string[] | null;
	focus_topics?: string[] | null;
	/** Quantos temas mostrar antes do "+N". */
	maxTopics?: number;
	/** Esconde a linha "Abordagem: ...". */
	hideApproach?: boolean;
	/** Esconde o chip de destaque (quando ele já aparece em outro lugar). */
	hideHighlight?: boolean;
	size?: ChipSize;
	/** Alinhamento das linhas (ex.: 'items-center' para cards centralizados). */
	className?: string;
}

/**
 * Bloco compacto de tags do profissional: chip de destaque, linha de abordagem
 * e chips de temas. Renderiza nada quando o profissional não tem tag alguma.
 */
const ProfessionalTagChips = ({
	highlight_tag,
	main_approach,
	secondary_approaches,
	focus_topics,
	maxTopics = 3,
	hideApproach = false,
	hideHighlight = false,
	size = 'sm',
	className,
}: ProfessionalTagChipsProps) => {
	const highlight = hideHighlight ? null : getHighlightTag(highlight_tag);
	const approachLine = hideApproach
		? ''
		: formatApproachShortLine(main_approach, secondary_approaches);
	const topics = (focus_topics ?? [])
		.map((key) => getFocusTopic(key))
		.filter((topic): topic is NonNullable<typeof topic> => Boolean(topic));
	const visibleTopics = topics.slice(0, maxTopics);
	const hiddenTopicsCount = topics.length - visibleTopics.length;

	if (!highlight && !approachLine && topics.length === 0) return null;

	const centered = className?.includes('items-center');

	return (
		<div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
			{highlight ? (
				<div
					className={cn(
						'flex',
						centered ? 'justify-center' : 'justify-start',
					)}
				>
					<HighlightTagChip tag={highlight.key} size={size} />
				</div>
			) : null}

			{approachLine ? (
				<p
					className={cn(
						'truncate text-[11px] font-semibold text-zinc-600',
						centered && 'text-center',
					)}
					title={approachLine}
				>
					<span className="font-black uppercase tracking-[0.14em] text-zinc-400">
						Abordagem:
					</span>{' '}
					{approachLine}
				</p>
			) : null}

			{visibleTopics.length > 0 ? (
				<div
					className={cn(
						'flex flex-wrap gap-1',
						centered && 'justify-center',
					)}
				>
					{visibleTopics.map((topic) => (
						<span
							key={topic.key}
							className={cn(
								'inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 font-bold text-zinc-600',
								size === 'sm'
									? 'px-2 py-0.5 text-[10px]'
									: 'px-2.5 py-1 text-[11px]',
							)}
						>
							{topic.label}
						</span>
					))}
					{hiddenTopicsCount > 0 ? (
						<span
							className={cn(
								'inline-flex items-center rounded-full border border-dashed border-zinc-300 font-bold text-zinc-400',
								size === 'sm'
									? 'px-2 py-0.5 text-[10px]'
									: 'px-2.5 py-1 text-[11px]',
							)}
							title={topics
								.slice(maxTopics)
								.map((t) => t.label)
								.join(', ')}
						>
							+{hiddenTopicsCount}
						</span>
					) : null}
				</div>
			) : null}
		</div>
	);
};

export { HighlightTagChip, ProfessionalTagChips, formatApproachShortLine };
